import { novelDramaPresetIssues, isNovelCharacterCardState } from '@drama/contracts/novel'
import { describePath } from './issue-paths.js'

const list = value => Array.isArray(value) ? value : []
const filled = value => typeof value === 'string' && value.trim().length > 0
const knownSelection = (value, ids) => list(value).length > 0 && list(value).every(id => ids.has(id))

/** Display grouping only; the server retains the authoritative checks for each formal action. */
export function supplementalGroupOfPath(path) {
  if (/^(narrative\.author_truths|scene_templates)(\.|$)/.test(path)) return 'narrative_advanced'
  if (/^facts(\.|$)/.test(path)) return 'facts'
  if (/^(items|initial_state\.items)(\.|$)/.test(path)) return 'items'
  if (/^relations(\.|$)/.test(path)) return 'relations'
  if (/^characters\.\d+\.drama_presets(\.|$)/.test(path)) return 'drama'
  if (/^characters\.\d+\.(profile|initial_goal|performance_states|performance_cues|style_examples)(\.|$)/.test(path)) return 'character_details'
  return null
}

/** Local completeness hints for authored optional entries, without adding a save or play gate. */
export function pendingSupplementalEntries(pack) {
  if (!pack) return []
  const pending = new Map()
  const people = list(pack.characters)
  const person_ids = new Set(people.map(person => person.id))
  const place_ids = new Set(list(pack.locations).map(place => place.id))
  const entity_ids = new Set([...person_ids, ...place_ids, ...list(pack.items).map(item => item.id)])
  function add(group_id, path, missing) {
    if (missing.length && !pending.has(path)) pending.set(path, { group_id, path, focus_path: missing[0] })
  }
  function texts(base, item, fields) {
    return fields.filter(field => !filled(item?.[field])).map(field => `${base}.${field}`)
  }

  list(pack.narrative?.author_truths).forEach((truth, index) => {
    const path = `narrative.author_truths.${index}`
    add('narrative_advanced', path, filled(truth) ? [] : [path])
  })
  list(pack.facts).forEach((fact, index) => {
    const path = `facts.${index}`
    const missing = texts(path, fact, ['text'])
    if (!knownSelection(fact.observer_ids, person_ids)) missing.push(`${path}.observer_ids`)
    add('facts', path, missing)
  })
  list(pack.items).forEach((item, index) => {
    const path = `items.${index}`
    const missing = texts(path, item, ['name', 'description'])
    const state = pack.initial_state?.items?.[item.id]
    if (!place_ids.has(state?.location_id)
      || (state?.holder_id && state.location_id !== pack.initial_state?.positions?.[state.holder_id]))
      missing.push(`initial_state.items.${item.id}.location_id`)
    if (state?.holder_id && !person_ids.has(state.holder_id)) missing.push(`initial_state.items.${item.id}.holder_id`)
    add('items', path, missing)
  })
  list(pack.relations).forEach((relation, index) => {
    const path = `relations.${index}`
    const missing = []
    if (!person_ids.has(relation.from_id)) missing.push(`${path}.from_id`)
    if (!person_ids.has(relation.to_id) || relation.from_id === relation.to_id) missing.push(`${path}.to_id`)
    missing.push(...texts(path, relation, ['text']))
    if (!knownSelection(relation.observer_ids, person_ids)) missing.push(`${path}.observer_ids`)
    add('relations', path, missing)
  })
  list(pack.scene_templates).forEach((scene, index) => {
    const path = `scene_templates.${index}`
    const missing = place_ids.has(scene.location_id) ? [] : [`${path}.location_id`]
    add('narrative_advanced', path, [...missing, ...texts(path, scene, ['situation', 'direction'])])
  })
  people.forEach((person, index) => {
    const base = `characters.${index}`
    list(person.performance_states).forEach((state, state_index) => {
      const path = `${base}.performance_states.${state_index}`
      add('character_details', path, texts(path, state, isNovelCharacterCardState(state) ? ['image'] : ['image', 'description']))
    })
    list(person.style_examples).forEach((example, example_index) => {
      const path = `${base}.style_examples.${example_index}`
      add('character_details', path, typeof example === 'string'
        ? filled(example) ? [] : [path] : texts(path, example, ['situation', 'response']))
    })
    list(person.performance_cues).forEach((cue, cue_index) => {
      const path = `${base}.performance_cues.${cue_index}`
      const missing = texts(path, cue, ['text'])
      if (cue.trigger) {
        if (!['topic', 'entity_mentioned'].includes(cue.trigger.kind)) missing.push(`${path}.trigger.kind`)
        if (cue.trigger.kind === 'entity_mentioned' && !entity_ids.has(cue.trigger.entity_id)) missing.push(`${path}.trigger.entity_id`)
        if (!list(cue.trigger.terms).length || !cue.trigger.terms.every(filled)) missing.push(`${path}.trigger.terms`)
        if (!Number.isInteger(cue.cooldown_turns) || cue.cooldown_turns < 1 || cue.cooldown_turns > 12) missing.push(`${path}.cooldown_turns`)
      }
      add('character_details', path, missing)
    })
  })
  for (const issue of novelDramaPresetIssues(pack)) {
    const path = issue.path.match(/^characters\.\d+\.drama_presets\.locations\.\d+/)?.[0]
      ?? issue.path.match(/^characters\.\d+\.drama_presets/)?.[0] ?? issue.path
    add('drama', path, [issue.path])
  }
  return [...pending.values()]
}

/** Reuse the existing issue list and navigation without presenting completeness hints as server errors. */
export function supplementalEntryIssues(pack, entries, translate) {
  return entries.map(entry => {
    const place = describePath(pack, entry.focus_path)
    return { key: `supplemental-${entry.path}`, path: entry.focus_path,
      section_id: entry.group_id === 'drama' ? 'drama' : place.section_id,
      text: [place.entry, place.field_key ? translate(place.field_key) : translate('novelAuthoring.pendingEntry')]
        .filter(Boolean).join(' · ') }
  })
}
